import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  login as apiLogin,
  logout as apiLogout,
  getMyCompanies,
  getMyPermissions,
  getMyCommandPolicyGroups,
} from '../services/api';
import { LoginRequest } from '../types';

type AccessLevel = 'None' | 'Read' | 'ReadWrite';

interface CompanyInfo {
  id: number;
  name: string;
  code: string;
}

interface AuthState {
  token: string | null;
  username: string | null;
  role: string | null;
  isSuperAdmin: boolean;
  companies: CompanyInfo[];
  activeCompanyId: number | null;
  permissions: Record<string, AccessLevel>;
  accessiblePlantIds: number[];
  /**
   * IDs dei CommandPolicyGroup di cui l'utente è membro. Usato per UX
   * (disabilita cursor pointer + click sui bottoni comando per variabili
   * non autorizzate). Il check di autorizzazione finale resta server-side
   * in WriteVariableCommand.
   * NB: il super-admin NON bypassa questo check — anche lui deve essere
   * esplicitamente membro del gruppo policy. Politica di sicurezza
   * "no implicit privilege": se Admin vuole comandare una variabile, va
   * iscritto al gruppo come tutti gli altri.
   */
  accessibleCommandPolicyGroupIds: number[];
  permissionsLoaded: boolean;
}

interface LoginResult {
  mustChangePassword?: boolean;
}

interface AuthContextValue extends AuthState {
  isAuthenticated: boolean;
  login: (request: LoginRequest) => Promise<LoginResult | void>;
  logout: () => Promise<void>;
  switchCompany: (companyId: number) => Promise<void>;
  hasPermission: (section: string, level?: AccessLevel) => boolean;
  canView: (section: string) => boolean;
  canEdit: (section: string) => boolean;
  /**
   * Check rapido se l'utente può eseguire un comando su una variabile.
   * Ritorna true sse:
   *  - la variabile ha commandEnabled=true
   *  - la variabile ha un commandPolicyGroupId assegnato (no fallback)
   *  - l'utente è membro di quel gruppo
   * Speculare al check server-side in WriteVariableCommand. Vedi commento
   * `accessibleCommandPolicyGroupIds`: il super-admin NON bypassa questo check.
   */
  canExecuteCommand: (variable: { commandEnabled?: boolean; commandPolicyGroupId?: number | null } | null | undefined) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function loadAuthState(): AuthState {
  return {
    token: localStorage.getItem('token'),
    username: localStorage.getItem('username'),
    role: localStorage.getItem('role'),
    isSuperAdmin: localStorage.getItem('isSuperAdmin') === 'true',
    companies: JSON.parse(localStorage.getItem('companies') || '[]'),
    activeCompanyId: parseInt(localStorage.getItem('activeCompanyId') || '0') || null,
    permissions: JSON.parse(localStorage.getItem('permissions') || '{}'),
    accessiblePlantIds: JSON.parse(localStorage.getItem('accessiblePlantIds') || '[]'),
    accessibleCommandPolicyGroupIds: JSON.parse(localStorage.getItem('accessibleCommandPolicyGroupIds') || '[]'),
    permissionsLoaded: false,
  };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [authState, setAuthState] = useState<AuthState>(loadAuthState);

  // Carica companies e permessi dopo il login o al refresh
  // Senza companyId: il backend aggrega tutti gli impianti di tutte le aziende
  const loadPermissions = useCallback(async (companyId?: number) => {
    try {
      const [companies, permsData, commandGroups] = await Promise.all([
        getMyCompanies(),
        getMyPermissions(), // Nessun companyId → il backend restituisce tutti gli impianti
        getMyCommandPolicyGroups(),
      ]);

      const activeId = companyId || permsData.companyId || companies[0]?.id || null;
      const permissions = permsData.permissions as Record<string, AccessLevel>;
      const accessiblePlantIds = permsData.accessiblePlantIds;
      const accessibleCommandPolicyGroupIds = commandGroups.map(g => g.id);

      localStorage.setItem('companies', JSON.stringify(companies));
      localStorage.setItem('activeCompanyId', String(activeId || ''));
      localStorage.setItem('permissions', JSON.stringify(permissions));
      localStorage.setItem('accessiblePlantIds', JSON.stringify(accessiblePlantIds));
      localStorage.setItem('accessibleCommandPolicyGroupIds', JSON.stringify(accessibleCommandPolicyGroupIds));

      setAuthState(prev => ({
        ...prev,
        companies,
        activeCompanyId: activeId,
        permissions,
        accessiblePlantIds,
        accessibleCommandPolicyGroupIds,
        permissionsLoaded: true,
      }));
    } catch (err) {
      console.error('Errore caricamento permessi:', err);
      setAuthState(prev => ({ ...prev, permissionsLoaded: true }));
    }
  }, []);

  // Al primo mount, se autenticato, carica permessi
  useEffect(() => {
    if (authState.token && !authState.permissionsLoaded) {
      loadPermissions(authState.activeCompanyId || undefined);
    }
  }, [authState.token, authState.permissionsLoaded, authState.activeCompanyId, loadPermissions]);

  const login = useCallback(async (request: LoginRequest): Promise<LoginResult | void> => {
    const response = await apiLogin(request);

    // Se il backend richiede cambio password, non salvare nulla e ritorna il flag
    if ((response as any).mustChangePassword) {
      return { mustChangePassword: true };
    }

    const isSuperAdmin = (response as unknown as { isSuperAdmin?: boolean }).isSuperAdmin === true
      || response.role === 'Admin'; // fallback

    localStorage.setItem('token', response.token);
    localStorage.setItem('username', response.username);
    localStorage.setItem('role', response.role);
    localStorage.setItem('isSuperAdmin', String(isSuperAdmin));

    setAuthState({
      token: response.token,
      username: response.username,
      role: response.role,
      isSuperAdmin,
      companies: [],
      activeCompanyId: null,
      permissions: {},
      accessiblePlantIds: [],
      accessibleCommandPolicyGroupIds: [],
      permissionsLoaded: false, // trigger useEffect
    });
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    const keys = ['token', 'username', 'role', 'isSuperAdmin', 'companies',
      'activeCompanyId', 'permissions', 'accessiblePlantIds', 'accessibleCommandPolicyGroupIds'];
    keys.forEach(k => localStorage.removeItem(k));
    setAuthState({
      token: null, username: null, role: null, isSuperAdmin: false,
      companies: [], activeCompanyId: null, permissions: {},
      accessiblePlantIds: [], accessibleCommandPolicyGroupIds: [], permissionsLoaded: false,
    });
  }, []);

  const switchCompany = useCallback(async (companyId: number) => {
    await loadPermissions(companyId);
  }, [loadPermissions]);

  const hasPermission = useCallback((section: string, level: AccessLevel = 'Read') => {
    if (authState.isSuperAdmin) return true;
    const perm = authState.permissions[section];
    if (!perm || perm === 'None') return false;
    if (level === 'Read') return perm === 'Read' || perm === 'ReadWrite';
    if (level === 'ReadWrite') return perm === 'ReadWrite';
    return false;
  }, [authState.isSuperAdmin, authState.permissions]);

  const canView = useCallback((section: string) => hasPermission(section, 'Read'), [hasPermission]);
  const canEdit = useCallback((section: string) => hasPermission(section, 'ReadWrite'), [hasPermission]);

  const canExecuteCommand = useCallback(
    (variable: { commandEnabled?: boolean; commandPolicyGroupId?: number | null } | null | undefined) => {
      if (!variable) return false;
      if (!variable.commandEnabled) return false;
      // Variabili senza gruppo policy non sono comandabili da nessuno (anche admin).
      // Coerente col check server-side in WriteVariableCommand.
      if (variable.commandPolicyGroupId == null) return false;
      return authState.accessibleCommandPolicyGroupIds.includes(variable.commandPolicyGroupId);
    },
    [authState.accessibleCommandPolicyGroupIds]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      ...authState,
      isAuthenticated: !!authState.token,
      login,
      logout,
      switchCompany,
      hasPermission,
      canView,
      canEdit,
      canExecuteCommand,
    }),
    [authState, login, logout, switchCompany, hasPermission, canView, canEdit, canExecuteCommand]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export default AuthContext;
